import React, { useState, useEffect } from 'react';
import { Leaf, Clock, AlertTriangle, FileBarChart, ChevronDown, ChevronUp } from 'lucide-react';

import PropertyCard from './ui/PropertyCard';
import DataItem from './ui/DataItem';
import RecordNavigator from './ui/RecordNavigator';
import { usePropertyFilters } from '../../contexts/PropertyFilterContext';
import { normalize } from '../map/LayerAliasUtils';
import Button from '@mui/material/Button';
import CarDashboardView from './CarDashboardView';

/**
 * Componente para visualização de dados do Cadastro Ambiental Rural (CAR)
 * de uma propriedade rural, mostrando detalhes da propriedade selecionada pelo usuário
 * e informações detalhadas dos temas ambientais.
 */
const CarTab = ({ propertyData, onHighlightGeometry }) => {
  // Estado para controlar a exibição do modal de dashboard consolidado
  const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
  // Colapso de detalhes do cabeçalho CAR
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);

  // Acesso ao contexto para saber qual código CAR está selecionado
  const { selectedCarCodes, setSelectedCarCodes } = usePropertyFilters();

  // Estado local para o registro CAR selecionado e índice atual
  const [selectedCarDetail, setSelectedCarDetail] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Atualiza o registro selecionado quando os dados mudam ou quando a seleção muda
  useEffect(() => {
    if (!propertyData || !propertyData.properties || !propertyData.properties.car) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedCarDetail(null);
      setTotalRecords(0);
      return;
    }

    // Normalizar para array, conforme padrão de databinding do projeto
    const carData = Array.isArray(propertyData.properties.car)
      ? propertyData.properties.car
      : [propertyData.properties.car];

    // Atualiza o total de registros
    setTotalRecords(carData.length);

    //console.log('[CarTab] Dados CAR disponíveis:', carData);

    // Log da estrutura principal dos dados para identificar padrões
    if (carData.length > 0) {
      //console.log('[CarTab] Estrutura do primeiro item CAR:',
      //            Object.keys(carData[0]).map(key => `${key}: ${typeof carData[0][key]}`));

      // Verifica se há um objeto 'details' (padrão comum no sistema)
      if (carData[0].details) {
        //console.log('[CarTab] Campos no objeto details:', Object.keys(carData[0].details));
      }
    }

    // Se houver um código selecionado no contexto, use-o para filtrar
    if (selectedCarCodes && selectedCarCodes.length > 0) {
      // Tenta encontrar pelo código exato
      let selected = carData.find((car) => car.cod_imovel === selectedCarCodes[0]);
      let selectedIndex = carData.findIndex((car) => car.cod_imovel === selectedCarCodes[0]);

      // Se não encontrar, tenta um match parcial
      if (!selected) {
        selected = carData.find(
          (car) =>
            car.cod_imovel && selectedCarCodes[0] && car.cod_imovel.includes(selectedCarCodes[0]),
        );
        selectedIndex = carData.findIndex(
          (car) =>
            car.cod_imovel && selectedCarCodes[0] && car.cod_imovel.includes(selectedCarCodes[0]),
        );
      }

      if (selected) {
        setSelectedCarDetail(selected);
        setCurrentIndex(selectedIndex !== -1 ? selectedIndex : 0);
        return;
      }
    }

    // Se não houver seleção ou a seleção não for encontrada, use o primeiro registro
    if (carData.length > 0) {
      //console.log('[CarTab] Usando primeiro registro CAR disponível:', carData[0]);
      setSelectedCarDetail(carData[0]);
      setCurrentIndex(0);
    } else {
      setSelectedCarDetail(null);
      setCurrentIndex(0);
    }
  }, [propertyData, selectedCarCodes]);

  // Diagnóstico
  //console.log('[CarTab] Estado:', {
  //  temDados: propertyData ? 'Sim' : 'Não',
  //  temCAR: propertyData?.properties?.car ? 'Sim' : 'Não',
  //  totalCarRegistros: Array.isArray(propertyData?.properties?.car)
  //    ? propertyData.properties.car.length
  //    : propertyData?.properties?.car ? 1 : 0,
  //  codigosSelecionados: selectedCarCodes,
  //  detalheSelecionado: selectedCarDetail ? selectedCarDetail.cod_imovel : 'Nenhum'
  //});

  // Log detalhado da estrutura do objeto
  if (selectedCarDetail) {
    //console.log('[CarTab] Estrutura do objeto selectedCarDetail:', selectedCarDetail);
    //console.log('[CarTab] Campos chave disponíveis:', Object.keys(selectedCarDetail));
  }

  // Verificação de dados
  if (!propertyData || !propertyData.properties || !propertyData.properties.car) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <p className="text-gray-500">Nenhum dado CAR disponível para esta propriedade.</p>
      </div>
    );
  }

  // Se não houver um registro selecionado
  if (!selectedCarDetail) {
    return (
      <div className="p-4 bg-gray-50 rounded border border-gray-200">
        <p className="text-gray-500">Selecione um registro CAR para visualizar seus detalhes.</p>
      </div>
    );
  }

  // Verificar se tem dados georreferenciados
  const _hasGeojson =
    selectedCarDetail.geojson ||
    selectedCarDetail.geometry ||
    selectedCarDetail.feature ||
    (selectedCarDetail.properties &&
      (selectedCarDetail.properties.geojson ||
        selectedCarDetail.properties.geometry ||
        selectedCarDetail.properties.feature));

  /**
   * Helper para obter um campo com suporte a vários nomes de campo possíveis e profundidade no objeto
   * Segue o padrão de acesso do projeto para propertyData
   */
  const getField = (obj, ...possibleKeys) => {
    if (!obj) return '';

    // Verifica em todos os níveis e com todas as chaves possíveis
    for (const key of possibleKeys) {
      // Nível direto
      if (obj[key] != null) return obj[key];

      // Nível details
      if (obj.details && obj.details[key] != null) return obj.details[key];

      // Nível attributes (GeoJSON/GeoServer)
      if (obj.attributes && obj.attributes[key] != null) return obj.attributes[key];

      // Nível properties (GeoJSON)
      if (obj.properties && obj.properties[key] != null) return obj.properties[key];

      // Verificar versão normalizada (sem acentos)
      const normalizedKey = normalize(key);
      for (const objKey of Object.keys(obj)) {
        if (normalize(objKey) === normalizedKey) return obj[objKey];
      }
    }

    return '';
  };

  /**
   * Converte string ou número para float com tratamento adequado
   * Força conversão de strings para números com tratamento de casos especiais
   */
  const getNumber = (obj, ...possibleKeys) => {
    const raw = getField(obj, ...possibleKeys);
    if (raw === '' || raw == null) return 0;

    // Tratar string 'nan' e valor numérico NaN
    if (
      (typeof raw === 'string' && raw.toLowerCase() === 'nan') ||
      (typeof raw === 'number' && isNaN(raw))
    ) {
      return 0;
    }

    // Garantir conversão de strings para números
    // Tratar formatos com vírgula como separador decimal (padrão brasileiro)
    if (typeof raw === 'string') {
      // Remover não-dígitos, exceto pontos e vírgulas
      const cleaned = raw.replace(/[^0-9.,]/g, '');
      // Substituir vírgula por ponto e converter
      const normalized = cleaned.replace(/,/g, '.');
      const n = parseFloat(normalized);
      return !isNaN(n) ? n : 0;
    }

    // Se já for número
    return typeof raw === 'number' ? raw : 0;
  };

  // Helper para formatar datas de forma segura
  function safeFormatDate(dateStr) {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '--' : d.toLocaleDateString('pt-BR');
  }

  // Extrair e normalizar campos usando o padrão correto de databinding
  const codImovel = getField(selectedCarDetail, 'cod_imovel', 'codigo', 'codigo_imovel');
  const uf = getField(selectedCarDetail, 'uf', 'UF');
  const municipio = getField(selectedCarDetail, 'municipio');

  // Campos com nomes alternativos usando o padrão de binding
  const situacaoField = getField(
    selectedCarDetail,
    'situacao_do_cadastro',
    'situação_do_cadastro',
    'situacao',
    'situação',
    'status',
  );
  const condicaoField = getField(
    selectedCarDetail,
    'condicao_do_cadastro',
    'condição_do_cadastro',
    'condicao',
    'condição',
  );
  const dataInscricaoField = getField(
    selectedCarDetail,
    'data_de_inscricao',
    'data_de_inscrição',
    'data_inscricao',
    'data_inscrição',
  );
  const dataAlteracaoCondicao = getField(
    selectedCarDetail,
    'data_de_alteracao_da_condicao_do_cadastro',
    'data_de_alteração_da_condição_do_cadastro',
    'data_alteracao_condicao',
    'data_alteração_condição',
  );
  const dataUltimaRetificacao = getField(
    selectedCarDetail,
    'data_da_ultima_retificacao',
    'data_da_última_retificação',
    'data_ultima_retificacao',
    'data_última_retificação',
  );
  const solicitacaoPra = getField(
    selectedCarDetail,
    'solicitacao_de_adesao_ao_pra',
    'solicitação_de_adesão_ao_pra',
  );
  const tipoImovel = getField(selectedCarDetail, 'tipo_de_imovel_rural', 'tipo_de_imóvel_rural');

  // Dados numéricos usando padrão de binding - obter área total da propriedade
  let areaTotalHa = getNumber(
    selectedCarDetail,
    'area_da_propriedade',
    'área_da_propriedade',
    'area_imovel',
    'área_imovel',
    'area_total',
    'área_total',
    'area_do_imovel',
    'área_do_imóvel',
  );

  // Log para diagnóstico da área total original
  //console.log(`[CarTab] Área Total detectada originalmente: ${areaTotalHa} ha`);
  const modulosFiscais = getNumber(
    selectedCarDetail,
    'modulos_fiscais',
    'módulos_fiscais',
    'modulo_fiscal',
    'módulo_fiscal',
  );
  const _reservaLegalHa = getNumber(
    selectedCarDetail,
    'area_de_reserva_legal_proposta',
    'área_de_reserva_legal_proposta',
  );
  const _appHa = getNumber(
    selectedCarDetail,
    'area_de_preservacao_permanente',
    'área_de_preservação_permanente',
  );
  const _vegetacaoNativaHa = getNumber(
    selectedCarDetail,
    'area_remanescente_de_vegetacao_nativa',
    'área_remanescente_de_vegetação_nativa',
  );
  const _areaConsolidadaHa = getNumber(
    selectedCarDetail,
    'area_rural_consolidada',
    'área_rural_consolidada',
  );

  // Função para destacar geometria no mapa
  const _handleHighlight = () => {
    if (onHighlightGeometry && selectedCarDetail) {
      onHighlightGeometry(selectedCarDetail);
    }
  };

  // Extrair mais campos para diagnóstico
  //console.log('[CarTab] Valores de campos críticos:',{
  //  cod_imovel: codImovel,
  //  uf: uf,
  //  situacao: situacaoField,
  //  condicao: condicaoField,
  //  area_total: areaTotalHa,
  //  modulos_fiscais: modulosFiscais
  //});

  // Dicionário com as áreas do CAR conforme propertyData.ts e LayerAliasUtils
  // Este mapeamento usa o dicionário de layerAliases e as nomeclaturas padrões
  const areaComposicao = [
    {
      key: 'area_de_pousio',
      accentKey: 'área_de_pousio',
      label: 'Pousio',
      color: 'bg-amber-400',
      alias: 'ÁREA DE POUSIO',
    },
    {
      key: 'area_de_preservacao_permanente',
      accentKey: 'área_de_preservação_permanente',
      label: 'Preservação Permanente',
      color: 'bg-blue-400',
      alias: 'APP',
    },
    {
      key: 'area_de_reserva_legal_aprovada_e_nao_averbada',
      accentKey: 'área_de_reserva_legal_aprovada_e_não_averbada',
      label: 'Reserva Legal Aprovada Não Averbada',
      color: 'bg-green-400',
      alias: 'RESERVA LEGAL',
    },
    {
      key: 'area_de_reserva_legal_averbada',
      accentKey: 'área_de_reserva_legal_averbada',
      label: 'Reserva Legal Averbada',
      color: 'bg-emerald-400',
      alias: 'RESERVA LEGAL',
    },
    {
      key: 'area_de_reserva_legal_proposta',
      accentKey: 'área_de_reserva_legal_proposta',
      label: 'Reserva Legal Proposta',
      color: 'bg-lime-400',
      alias: 'RESERVA LEGAL',
    },
    {
      key: 'area_de_servidao_administrativa',
      accentKey: 'área_de_servidão_administrativa',
      label: 'Servidão Administrativa',
      color: 'bg-violet-400',
      alias: 'ÁREA DE SERVIDÃO',
    },
    {
      key: 'area_de_uso_restrito',
      accentKey: 'área_de_uso_restrito',
      label: 'Uso Restrito',
      color: 'bg-rose-400',
      alias: 'USO RESTRITO',
    },
    {
      key: 'area_liquida',
      accentKey: 'área_líquida',
      label: 'Área Líquida',
      color: 'bg-cyan-400',
      alias: '',
    },
    {
      key: 'area_nao_classificada',
      accentKey: 'área_não_classificada',
      label: 'Não Classificada',
      color: 'bg-gray-400',
      alias: '',
    },
    {
      key: 'area_remanescente_de_vegetacao_nativa',
      accentKey: 'área_remanescente_de_vegetação_nativa',
      label: 'Vegetação Nativa',
      color: 'bg-teal-400',
      alias: 'VEGETAÇÃO NATIVA',
    },
    {
      key: 'area_rural_consolidada',
      accentKey: 'área_rural_consolidada',
      label: 'Rural Consolidada',
      color: 'bg-yellow-400',
      alias: 'ÁREA CONSOLIDADA',
    },
  ];

  // Primeiro, coleta todos os valores das áreas para cálculo
  const areasValores = areaComposicao.map((item) => {
    return getNumber(
      selectedCarDetail,
      item.key,
      item.accentKey,
      `details.${item.key}`,
      `details.${item.accentKey}`,
    );
  });

  // Se a área total estiver zerada ou muito pequena, usa a soma das áreas como referência
  if (areaTotalHa < 1) {
    const somaAreas = areasValores.reduce((acc, val) => acc + val, 0);
    if (somaAreas > 0) {
      areaTotalHa = somaAreas;
      //console.log(`[CarTab] Área Total recalculada usando soma das áreas: ${areaTotalHa.toFixed(2)} ha`);
    } else {
      //console.log(`[CarTab] AVISO: Não foi possível determinar a área total!`);
    }
  }

  // Cálculos baseados na área total da propriedade (original ou recalculada)
  const composicaoData = areaComposicao.map((item, index) => {
    // Usamos o valor já calculado anteriormente
    const valor = areasValores[index];

    // Calcular percentual em relação à área total da propriedade
    const percent = areaTotalHa > 0 ? (valor / areaTotalHa) * 100 : 0;

    // Log detalhado de cada cálculo para diagnóstico
    //console.log(`[CarTab] Composição: ${item.label} = ${valor} ha (${percent.toFixed(2)}% da área total de ${areaTotalHa} ha)`);

    return {
      ...item,
      valor: valor || 0,
      percent: percent || 0,
      valorFormatado:
        valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
        ' ha',
    };
  });

  // Divide em colunas dinâmicas baseado no tamanho do array
  function splitInColumns(arr, numCols) {
    // Ajusta o número de colunas se houver poucos itens
    const effectiveCols = arr.length < numCols ? arr.length : numCols;

    // Se não houver itens, retorna array vazio
    if (arr.length === 0) return [];

    const cols = Array.from({ length: effectiveCols }, () => []);
    arr.forEach((item, idx) => {
      cols[idx % effectiveCols].push(item);
    });
    return cols;
  }
  const _composicaoCols = splitInColumns(composicaoData, 3);

  return (
    <div className="space-y-4">
      {/* Modal para exibir o dashboard consolidado */}
      <CarDashboardView
        open={dashboardModalOpen}
        onClose={() => setDashboardModalOpen(false)}
        propertyData={propertyData}
      />

      {/* Cabeçalho */}
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Leaf className="text-green-600 mr-2" size={20} />
            <h3 className="text-lg font-medium text-green-800">Cadastro Ambiental Rural</h3>
            {totalRecords > 1 && (
              <div className="ml-3 inline-flex">
                <RecordNavigator
                  currentIndex={currentIndex}
                  totalRecords={totalRecords}
                  className="bg-white/80 border border-gray-200 rounded-full shadow-sm"
                  onNavigate={(newIndex) => {
                    setCurrentIndex(newIndex);
                    const carData = Array.isArray(propertyData.properties.car)
                      ? propertyData.properties.car
                      : [propertyData.properties.car];
                    setSelectedCarDetail(carData[newIndex]);
                    if (carData[newIndex].cod_imovel) {
                      setSelectedCarCodes([carData[newIndex].cod_imovel]);
                    }
                  }}
                />
              </div>
            )}
          </div>

          <div className="inline-flex space-x-2">
            {totalRecords > 1 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileBarChart size={16} />}
                sx={{
                  height: '32px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  padding: '0px 12px',
                  width: '120px',
                }}
                onClick={() => setDashboardModalOpen(true)}
              >
                Consolidado
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center"></div>
      </div>

      <PropertyCard subtitle="CAR" color="green">
        {/* Cabeçalho compacto com possibilidade de colapso */}
        <div className="flex justify-end items-center mb-2">
          <button
            onClick={() => setDetailsCollapsed((v) => !v)}
            className="text-xs text-gray-600 hover:text-green-700 inline-flex items-center"
          >
            {detailsCollapsed ? (
              <ChevronDown size={14} className="mr-1" />
            ) : (
              <ChevronUp size={14} className="mr-1" />
            )}
            {detailsCollapsed ? 'Mostrar' : 'Ocultar'} detalhes
          </button>
        </div>

        {!detailsCollapsed && (
          <div className="space-y-2 mb-3">
            {/* Linha 1: Código (2 col) + Município + UF */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="md:col-span-2">
                <DataItem
                  icon={<Leaf size={16} className="text-green-600" />}
                  label="Código do Imóvel"
                  value={codImovel}
                  isCopyable
                  highlight
                  compact
                />
              </div>
              <DataItem label="Município" value={municipio || '--'} compact />
              <DataItem label="UF" value={uf || '--'} compact />
            </div>

            {/* Linha 2: Tipo, Módulos, Situação, Condição */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <DataItem label="Tipo de Imóvel Rural" value={tipoImovel || '--'} compact />
              <DataItem
                label="Módulos Fiscais"
                value={
                  typeof modulosFiscais === 'number'
                    ? modulosFiscais.toLocaleString('pt-BR', { minimumFractionDigits: 4 })
                    : '--'
                }
                compact
              />
              <DataItem
                label="Situação"
                value={situacaoField === 'AT' ? 'Ativo' : situacaoField || '--'}
                compact
              />
              <DataItem
                icon={<AlertTriangle size={16} className="text-yellow-500" />}
                label="Condição"
                value={condicaoField || '--'}
                compact
              />
            </div>

            {/* Linha 3: PRA, Datas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <DataItem
                label="Solicitação de Adesão ao PRA"
                value={solicitacaoPra || '--'}
                compact
              />
              <DataItem
                icon={<Clock size={16} />}
                label="Data de Inscrição"
                value={safeFormatDate(dataInscricaoField)}
                compact
              />
              <DataItem
                icon={<Clock size={16} />}
                label="Data de Alteração da Condição"
                value={safeFormatDate(dataAlteracaoCondicao)}
                compact
              />
              <DataItem
                icon={<Clock size={16} />}
                label="Data da Última Retificação"
                value={safeFormatDate(dataUltimaRetificacao)}
                compact
              />
            </div>
          </div>
        )}

        {/* Seções laterais: Área da Propriedade | Ambiental | APP */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="bg-gray-50 p-2 rounded-lg border border-gray-200 h-full">
            <h4 className="font-medium text-xs text-gray-700 mb-1">Área da Propriedade</h4>
            <div className="grid grid-cols-1 gap-2">
              {/* Área Total (sem gráfico de barra pois é referência) */}
              <div className="bg-white rounded-md px-2 py-1 flex flex-col border border-gray-100">
                <span className="text-[11px] text-gray-600 font-medium mb-0.5">Área Total</span>
                <span className="text-sm font-bold text-gray-900">
                  {typeof areaTotalHa === 'number'
                    ? areaTotalHa.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) + ' ha'
                    : '--'}
                </span>
              </div>

              {/* Demais áreas com gráficos de barra */}
              {composicaoData
                .filter((item) =>
                  [
                    'area_rural_consolidada',
                    'area_de_servidao_administrativa',
                    'area_nao_classificada',
                    'area_de_pousio',
                    'area_liquida',
                  ].includes(item.key),
                )
                .map((item) => (
                  <div
                    key={item.key}
                    className="bg-white rounded-md px-2 py-1 flex flex-col border border-gray-100"
                  >
                    <span className="text-[11px] text-gray-600 font-medium mb-0.5">
                      {item.label}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{item.valorFormatado}</span>
                    <div className="w-full h-1.5 rounded bg-gray-200 mt-0.5 mb-0.5">
                      <div
                        className={`h-1.5 rounded ${item.color}`}
                        style={{ width: `${Math.max(1, Math.min(100, item.percent))}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-500">{item.percent.toFixed(1)}%</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Seção: Ambiental */}
          <div className="bg-gray-50 p-2 rounded-lg border border-gray-200 h-full">
            <h4 className="font-medium text-xs text-gray-700 mb-1">Ambiental</h4>
            <div className="grid grid-cols-1 gap-2">
              {composicaoData
                .filter((item) =>
                  [
                    'area_de_reserva_legal_proposta',
                    'area_de_reserva_legal_aprovada_e_nao_averbada',
                    'area_de_reserva_legal_averbada',
                    'area_remanescente_de_vegetacao_nativa',
                    'area_de_uso_restrito',
                  ].includes(item.key),
                )
                .map((item) => (
                  <div
                    key={item.key}
                    className="bg-white rounded-md px-2 py-1 flex flex-col border border-gray-100"
                  >
                    <span className="text-[11px] text-gray-600 font-medium mb-0.5">
                      {item.label}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{item.valorFormatado}</span>
                    <div className="w-full h-1.5 rounded bg-gray-200 mt-0.5 mb-0.5">
                      <div
                        className={`h-1.5 rounded ${item.color}`}
                        style={{ width: `${Math.max(1, Math.min(100, item.percent))}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-500">{item.percent.toFixed(1)}%</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Seção: APP */}
          <div className="bg-gray-50 p-2 rounded-lg border border-gray-200 h-full">
            <h4 className="font-medium text-xs text-gray-700 mb-1">APP</h4>
            <div className="grid grid-cols-1 gap-2">
              {composicaoData
                .filter((item) => ['area_de_preservacao_permanente'].includes(item.key))
                .map((item) => (
                  <div
                    key={item.key}
                    className="bg-white rounded-md px-2 py-1 flex flex-col border border-gray-100"
                  >
                    <span className="text-[11px] text-gray-600 font-medium mb-0.5">
                      {item.label}
                    </span>
                    <span className="text-sm font-bold text-gray-900">{item.valorFormatado}</span>
                    <div className="w-full h-1.5 rounded bg-gray-200 mt-0.5 mb-0.5">
                      <div
                        className={`h-1.5 rounded ${item.color}`}
                        style={{ width: `${Math.max(1, Math.min(100, item.percent))}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-500">{item.percent.toFixed(1)}%</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </PropertyCard>
    </div>
  );
};

export default CarTab;
